import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Descriptions,
  Drawer,
  Dropdown,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  EyeOutlined,
  FilterOutlined,
  KeyOutlined,
  MoreOutlined,
  ReloadOutlined,
  StopOutlined,
} from "@ant-design/icons";
import {
  getAdminApiKey,
  getAdminApiKeys,
  normalizeAdminApiKey,
  normalizeAdminApiKeys,
  revokeAdminApiKey,
} from "../../services/apiKeyService";
import { formatDate } from "../../utils/format";

const { Text, Title } = Typography;
const { Option } = Select;

const formatUnixSeconds = (timestamp) => {
  if (!timestamp) return "N/A";

  const date = new Date(Number(timestamp) * 1000);
  if (Number.isNaN(date.getTime())) return "N/A";

  return date.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const getStatusBadge = (status) => {
  const statuses = {
    active: "success",
    expired: "warning",
    revoked: "error",
  };

  return statuses[status] || "default";
};

const AdminApiKeys = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedApiKey, setSelectedApiKey] = useState(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [revokeModalVisible, setRevokeModalVisible] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [apiKeyToRevoke, setApiKeyToRevoke] = useState(null);
  const [filters, setFilters] = useState({});
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterForm] = Form.useForm();
  const [revokeForm] = Form.useForm();
  const [nextCursor, setNextCursor] = useState(undefined);
  const [loadingMore, setLoadingMore] = useState(false);
  const tableWrapRef = useRef(null);
  const nextCursorRef = useRef(undefined);
  const loadingMoreRef = useRef(false);

  // Cursor-based pagination: the backend returns a page (server size 15) plus a
  // next_cursor. status is filtered server-side; search stays client-side since
  // the endpoint has no search param.
  const fetchApiKeys = useCallback(
    async (cursor) => {
      try {
        cursor ? setLoadingMore(true) : setLoading(true);

        const params = {};
        if (filters.status) params.status = filters.status;
        if (cursor) params.next_cursor = cursor;

        const data = await getAdminApiKeys(params);
        const page = normalizeAdminApiKeys(data);

        setApiKeys((prev) => (cursor ? [...prev, ...page] : page));
        setNextCursor(data?.next_cursor);
      } catch (error) {
        console.error("Error fetching API keys:", error);
        message.error("Failed to fetch API keys. Please try again.");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [filters.status]
  );

  useEffect(() => {
    // Reset to the first page whenever the server-side status filter changes.
    fetchApiKeys();
  }, [fetchApiKeys]);

  // Keep refs in sync so the scroll handler always reads current values.
  useEffect(() => {
    nextCursorRef.current = nextCursor;
  }, [nextCursor]);

  useEffect(() => {
    loadingMoreRef.current = loadingMore;
  }, [loadingMore]);

  // Infinite scroll: fetch the next page (via next_cursor) as the user nears the
  // bottom of the table body — no pagination control needed.
  useEffect(() => {
    const body = tableWrapRef.current?.querySelector(".ant-table-body");
    if (!body) return undefined;

    const handleScroll = () => {
      const reachedBottom =
        body.scrollHeight - body.scrollTop - body.clientHeight < 80;
      if (reachedBottom && nextCursorRef.current && !loadingMoreRef.current) {
        fetchApiKeys(nextCursorRef.current);
      }
    };

    body.addEventListener("scroll", handleScroll);
    return () => body.removeEventListener("scroll", handleScroll);
  }, [fetchApiKeys, apiKeys.length]);

  // Auto-refresh when the admin returns to this tab (e.g. after creating or
  // revoking a key on the customer dashboard in another tab), so the list is
  // never stale without a manual page refresh.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchApiKeys();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleVisibility);
    };
  }, [fetchApiKeys]);

  const filteredApiKeys = useMemo(() => {
    const search = filters.search?.trim().toLowerCase();
    if (!search) return apiKeys;

    return apiKeys.filter((apiKey) =>
      [apiKey.label, apiKey.id, apiKey.user_id, apiKey.profile_id].some(
        (value) => value?.toString().toLowerCase().includes(search)
      )
    );
  }, [apiKeys, filters.search]);

  const hasActiveFilters = useMemo(
    () => Object.values(filters).some((value) => value),
    [filters]
  );

  const handleViewDetails = async (apiKey) => {
    try {
      setDetailsLoading(true);
      setDetailsVisible(true);
      const data = await getAdminApiKey(apiKey.id);
      setSelectedApiKey(normalizeAdminApiKey(data));
    } catch (error) {
      console.error("Error fetching API key details:", error);
      message.error("Failed to fetch API key details. Please try again.");
      setSelectedApiKey(apiKey);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleOpenRevokeModal = (apiKey) => {
    setApiKeyToRevoke(apiKey);
    revokeForm.setFieldsValue({ revoked_reason: "" });
    setRevokeModalVisible(true);
  };

  const handleRevokeApiKey = async (values) => {
    if (!apiKeyToRevoke?.id) {
      message.error("No API key selected.");
      return;
    }

    try {
      setRevoking(true);
      await revokeAdminApiKey(
        apiKeyToRevoke.id,
        values.revoked_reason.trim()
      );
      message.success("API key revoked successfully");
      setRevokeModalVisible(false);
      setApiKeyToRevoke(null);
      revokeForm.resetFields();
      fetchApiKeys();
    } catch (error) {
      console.error("Error revoking API key:", error);
      message.error("Failed to revoke API key. Please try again.");
    } finally {
      setRevoking(false);
    }
  };

  const handleFilterSubmit = (values) => {
    setFilters({
      search: values.search?.trim() || undefined,
      status: values.status,
    });
    setFilterVisible(false);
  };

  const handleClearFilters = () => {
    filterForm.resetFields();
    setFilters({});
    setFilterVisible(false);
  };

  const getActionItems = (record) => {
    const items = [
      {
        key: "view",
        icon: <EyeOutlined />,
        label: "View Details",
        onClick: () => handleViewDetails(record),
      },
    ];

    if (record.status !== "revoked") {
      items.push({
        key: "revoke",
        icon: <StopOutlined style={{ color: "#ff4d4f" }} />,
        label: "Revoke API Key",
        onClick: () => handleOpenRevokeModal(record),
      });
    }

    return items;
  };

  const columns = [
    {
      title: "Label",
      dataIndex: "label",
      key: "label",
      width: 180,
      ellipsis: true,
      render: (label) => (
        <Space>
          <KeyOutlined />
          <Text style={{ maxWidth: 130 }} ellipsis={{ tooltip: label }}>
            {label || "N/A"}
          </Text>
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => (
        <Badge
          status={getStatusBadge(status)}
          text={status?.toUpperCase() || "N/A"}
        />
      ),
    },
    {
      title: "Profile ID",
      dataIndex: "profile_id",
      key: "profile_id",
      width: 190,
      render: (profileId) =>
        profileId ? (
          <Text
            copyable
            style={{ maxWidth: 155 }}
            ellipsis={{ tooltip: profileId }}
          >
            {profileId}
          </Text>
        ) : (
          "N/A"
        ),
    },
    {
      title: "Expires",
      dataIndex: "expiration_timestamp",
      key: "expiration_timestamp",
      width: 155,
      render: formatUnixSeconds,
    },
    {
      title: "Created",
      dataIndex: "created",
      key: "created",
      width: 155,
      render: (created) => formatDate(created) || "N/A",
    },
    {
      title: "Revoked Reason",
      dataIndex: "revoked_reason",
      key: "revoked_reason",
      width: 210,
      ellipsis: true,
      render: (reason) =>
        reason ? (
          <Text style={{ maxWidth: 180 }} ellipsis={{ tooltip: reason }}>
            {reason}
          </Text>
        ) : (
          <Text type="secondary">N/A</Text>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => (
        <Dropdown
          menu={{ items: getActionItems(record) }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button
            className="api-key-actions-button"
            type="text"
            icon={<MoreOutlined />}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <Card
        className="api-keys-card"
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>
              API Keys
            </Title>
            <Tag>{filteredApiKeys.length}</Tag>
          </Space>
        }
        extra={
          <Space>
            <Badge dot={hasActiveFilters} offset={[-5, 5]} color="#1890ff">
              <Button
                icon={<FilterOutlined />}
                onClick={() => setFilterVisible(true)}
                type={hasActiveFilters ? "primary" : "default"}
              >
                Filter
              </Button>
            </Badge>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchApiKeys()}
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
        }
      >
        <div className="table-responsive api-keys-table" ref={tableWrapRef}>
          <Table
            columns={columns}
            dataSource={filteredApiKeys}
            rowKey="id"
            loading={loading}
            tableLayout="fixed"
            pagination={false}
            scroll={{ x: 1090, y: 480 }}
          />
          <div style={{ marginTop: 12, textAlign: "center", minHeight: 22 }}>
            {loadingMore ? (
              <Text type="secondary">Loading more…</Text>
            ) : (
              !loading &&
              !nextCursor &&
              filteredApiKeys.length > 0 && (
                <Text type="secondary">End of list</Text>
              )
            )}
          </div>
        </div>
      </Card>

      <Drawer
        title="Filter API Keys"
        placement="right"
        onClose={() => setFilterVisible(false)}
        open={filterVisible}
        width={400}
      >
        <Form
          form={filterForm}
          layout="vertical"
          initialValues={filters}
          onFinish={handleFilterSubmit}
        >
          <Form.Item name="search" label="Search">
            <Input placeholder="Search by label, key ID, user ID, or profile ID" />
          </Form.Item>

          <Form.Item name="status" label="Status">
            <Select placeholder="Select status" allowClear>
              <Option value="active">Active</Option>
              <Option value="expired">Expired</Option>
              <Option value="revoked">Revoked</Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={handleClearFilters}>Clear</Button>
              <Button type="primary" htmlType="submit">
                Apply Filters
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>

      <Drawer
        title="API Key Details"
        placement="right"
        onClose={() => {
          setDetailsVisible(false);
          setSelectedApiKey(null);
        }}
        open={detailsVisible}
        width={560}
        loading={detailsLoading}
      >
        <Descriptions bordered column={1}>
          <Descriptions.Item label="ID">
            {selectedApiKey?.id ? (
              <Text copyable>{selectedApiKey.id}</Text>
            ) : (
              "N/A"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Label">
            {selectedApiKey?.label || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Badge
              status={getStatusBadge(selectedApiKey?.status)}
              text={selectedApiKey?.status?.toUpperCase() || "N/A"}
            />
          </Descriptions.Item>
          <Descriptions.Item label="User ID">
            {selectedApiKey?.user_id ? (
              <Text copyable>{selectedApiKey.user_id}</Text>
            ) : (
              "N/A"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Profile ID">
            {selectedApiKey?.profile_id ? (
              <Text copyable>{selectedApiKey.profile_id}</Text>
            ) : (
              "N/A"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Expires">
            {formatUnixSeconds(selectedApiKey?.expiration_timestamp)}
          </Descriptions.Item>
          <Descriptions.Item label="Created">
            {formatDate(selectedApiKey?.created) || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Revoked Reason">
            {selectedApiKey?.revoked_reason || "N/A"}
          </Descriptions.Item>
        </Descriptions>
      </Drawer>

      <Modal
        title="Revoke API Key"
        open={revokeModalVisible}
        onCancel={() => {
          setRevokeModalVisible(false);
          setApiKeyToRevoke(null);
          revokeForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form form={revokeForm} layout="vertical" onFinish={handleRevokeApiKey}>
          <Form.Item>
            <Text>
              This will revoke the API key{" "}
              <strong>{apiKeyToRevoke?.label || apiKeyToRevoke?.id}</strong>.
            </Text>
          </Form.Item>

          <Form.Item
            name="revoked_reason"
            label="Reason"
            rules={[
              { required: true, message: "Please enter a reason" },
              {
                validator: (_, value) =>
                  value?.trim()
                    ? Promise.resolve()
                    : Promise.reject(new Error("Please enter a reason")),
              },
            ]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Enter reason for revoking this API key"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button
                onClick={() => {
                  setRevokeModalVisible(false);
                  setApiKeyToRevoke(null);
                  revokeForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button
                danger
                type="primary"
                htmlType="submit"
                loading={revoking}
              >
                Revoke API Key
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default AdminApiKeys;
